<template>
<div class="bg-gradient-to-br from-gray-50 to-red-50 py-16 sm:py-24 relative overflow-hidden">
  <!-- Background pattern -->
  <div class="absolute inset-0 opacity-5">
    <div class="absolute inset-0" style="background-image: url('data:image/svg+xml,%3Csvg width=\'60\' height=\'60\' viewBox=\'0 0 60 60\' xmlns=\'http://www.w3.org/2000/svg\'%3E%3Cg fill=\'none\' fill-rule=\'evenodd\'%3E%3Cg fill=\'%23dc2626\' fill-opacity=\'0.1\'%3E%3Ccircle cx=\'30\' cy=\'30\' r=\'2\'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E');"></div>
  </div>

  <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
    <div class="text-center mb-16">
      <div class="inline-flex items-center px-4 py-2 rounded-full bg-red-100 border border-red-200 mb-6">
        <svg class="w-5 h-5 text-red-600 mr-2" fill="currentColor" viewBox="0 0 24 24">
          <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"/>
        </svg>
        <span class="text-red-800 text-sm font-medium">Contenuti Video</span>
      </div>
      
      <h2 class="text-3xl font-extrabold text-gray-900 sm:text-4xl mb-4">
        <span class="block">Ultimi Video</span>
        <span class="block text-transparent bg-clip-text bg-gradient-to-r from-red-600 to-red-800">su YouTube</span>
      </h2>
      <p class="mt-4 text-lg text-gray-600 max-w-2xl mx-auto">
        Segui i miei ultimi contenuti su YouTube. Workout, consigli nutrizionali, motivazione e molto altro per il tuo percorso fitness.
      </p>
    </div>

    <div class="grid gap-8 md:grid-cols-2 lg:grid-cols-3">
      <div v-for="video in videosWithEmbedUrls" :key="video.id" 
           class="bg-white rounded-2xl shadow-lg hover:shadow-2xl transform hover:-translate-y-2 transition-all duration-300 border border-gray-100 overflow-hidden group">
        <div class="relative">
          <!-- Clickable YouTube Embed -->
          <a :href="video.url" target="_blank" rel="noopener noreferrer" class="block relative w-full h-48 overflow-hidden cursor-pointer">
            <iframe
              :src="video.embedUrl"
              :title="video.title"
              class="w-full h-full rounded-t-2xl group-hover:scale-105 transition-transform duration-500 pointer-events-none"
              frameborder="0"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowfullscreen>
            </iframe>
            
            <!-- Clickable overlay with play button -->
            <div class="absolute inset-0 bg-black/0 hover:bg-black/20 transition-all duration-300 flex items-center justify-center group-hover:opacity-100">
              <div class="bg-white/90 backdrop-blur-sm rounded-full p-3 transform scale-0 group-hover:scale-100 transition-transform duration-300">
                <svg class="w-6 h-6 text-red-600" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M8 5v14l11-7z"/>
                </svg>
              </div>
            </div>
            
            <!-- Duration badge -->
            <div class="absolute bottom-3 right-3 bg-black/70 backdrop-blur-sm text-white px-2 py-1 rounded text-xs font-medium z-10">
              {{ video.duration }}
            </div>
          </a>
        </div>
        
        <div class="p-6">
          <h3 class="text-lg font-semibold text-gray-900 mb-3 line-clamp-2 group-hover:text-red-600 transition-colors duration-200">
            {{ video.title }}
          </h3>
          <p class="text-sm text-gray-600 mb-4 line-clamp-2">
            {{ video.description }}
          </p>
          
          <div class="flex items-center justify-between mb-4 text-xs text-gray-500">
            <span class="flex items-center">
              <svg class="w-4 h-4 mr-1" fill="currentColor" viewBox="0 0 20 20">
                <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-12a1 1 0 10-2 0v4a1 1 0 00.293.707l2.828 2.829a1 1 0 101.415-1.415L11 9.586V6z" clip-rule="evenodd" />
              </svg>
              {{ video.publishedAt }}
            </span>
            <span class="flex items-center">
              <svg class="w-4 h-4 mr-1" fill="currentColor" viewBox="0 0 20 20">
                <path d="M10 12a2 2 0 100-4 2 2 0 000 4z" />
                <path fill-rule="evenodd" d="M.458 10C1.732 5.943 5.522 3 10 3s8.268 2.943 9.542 7c-1.274 4.057-5.064 7-9.542 7S1.732 14.057.458 10zM14 10a4 4 0 11-8 0 4 4 0 018 0z" clip-rule="evenodd" />
              </svg>
              {{ video.isShort ? 'Shorts' : 'Video' }}
            </span>
          </div>
          
          <a :href="video.url" target="_blank" rel="noopener noreferrer"
             class="inline-flex items-center justify-center w-full px-4 py-3 border border-transparent text-sm font-medium rounded-xl text-white bg-gradient-to-r from-red-600 to-red-700 hover:from-red-700 hover:to-red-800 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 transform transition-all duration-200 hover:scale-105 shadow-md hover:shadow-lg">
            <svg class="mr-2 h-4 w-4" fill="currentColor" viewBox="0 0 24 24">
              <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"/>
            </svg>
            Guarda su YouTube
          </a>
        </div>
      </div>
    </div>

    <!-- YouTube Channel Link -->
    <div class="mt-16 text-center">
      <div class="bg-white rounded-2xl shadow-lg p-8 border border-gray-100 max-w-2xl mx-auto">
        <div class="flex items-center justify-center mb-4">
          <div class="w-16 h-16 bg-gradient-to-br from-red-600 to-red-700 rounded-full flex items-center justify-center mr-4">
            <svg class="w-8 h-8 text-white" fill="currentColor" viewBox="0 0 24 24">
              <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"/>
            </svg>
          </div>
          <div class="text-left">
            <h3 class="text-xl font-bold text-gray-900">Dott. Daniele Ciceri</h3>
            <p class="text-gray-600">Personal Trainer & Fitness Coach</p>
          </div>
        </div>
        
        <p class="text-gray-600 mb-6">
          Iscriviti al canale per non perdere nessun contenuto. Nuovi video ogni settimana con workout, consigli e motivazione!
        </p>
        
        <a href="https://www.youtube.com/@DanieleCiceri/videos" target="_blank" rel="noopener noreferrer"
           class="inline-flex items-center px-8 py-4 border border-transparent text-base font-medium rounded-xl text-white bg-gradient-to-r from-red-600 to-red-700 hover:from-red-700 hover:to-red-800 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 transform transition-all duration-200 hover:scale-105 shadow-lg hover:shadow-xl">
          <svg class="mr-2 h-5 w-5" fill="currentColor" viewBox="0 0 24 24">
            <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"/>
          </svg>
          Visita il Canale YouTube
        </a>
      </div>
    </div>
  </div>
</div>
</template>

<script>
export default {
  name: "YouTubeFeed",
  data() {
    return {
      videos: [
        {
          id: 'video1',
          title: "Quando la mano è calda… non sbaglio mai! 🔥🏀",
          description: "",
          videoId: 'KLAl7QoKTy8',
          duration: "18:45",
          publishedAt: "3 giorni fa",
          url: "https://youtu.be/KLAl7QoKTy8?si=E4zMYXff7BS6isM6",
          isShort: false
        },
        {
          id: 'video2',
          title: "Exclusive Interview with Dr. Daniele Ciceri | Pre-Match Insights: Como vs Inter @SuperSoccerTV",
          description: "",
          videoId: 'GlW4i8pdbIA',
          duration: "0:30",
          publishedAt: "1 settimana fa",
          url: "https://youtu.be/GlW4i8pdbIA?si=SZoWi_RLW4OvOe6P",
          isShort: false
        },
        {
          id: 'video3',
          title: "Luxury Escape to Corfu | 5-Star Akrotiri Hotel Experience",
          description: "",
          videoId: '6IoqGXkWIKE',
          duration: "12:18",
          publishedAt: "2 settimane fa",
          url: "https://youtu.be/6IoqGXkWIKE?si=YyYQ1fMOVu_AFSHp",
          isShort: false
        },
        {
          id: 'video4',
          title: "Intense Tabata Workout with Pietro Aradori | Summer Grind 2019",
          description: "",
          videoId: 'mBHUC_5fX74',
          duration: "0:45",
          publishedAt: "3 settimane fa",
          url: "https://youtu.be/mBHUC_5fX74?si=FawcRiwxsjFjwu7v",
          isShort: false
        },
        {
          id: 'video5',
          title: "Arms Day 💪🏻 #fitness #workout #personaltrainer",
          description: "",
          videoId: 'UiCXmzjdOGw',
          duration: "22:15",
          publishedAt: "1 mese fa",
          url: "https://youtu.be/UiCXmzjdOGw?si=WoJdrqHVnI8gLI87",
          isShort: false
        },
        {
          id: 'video6',
          title: "BACK WORKOUT at @actionfit Gallarate | Lat exercise 💪🏻 #workout #fitnessmotivation",
          description: "",
          videoId: 'A31MfdPvf5c',
          duration: "0:35",
          publishedAt: "1 mese fa",
          url: "https://youtu.be/A31MfdPvf5c?si=2CNoarDY7dQpjLUo",
          isShort: true
        }
      ]
    };
  },
  computed: {
    videosWithEmbedUrls() {
      return this.videos.map(video => ({
        ...video,
        embedUrl: `https://www.youtube.com/embed/${video.videoId}?rel=0&modestbranding=1`
      }));
    }
  }
};
</script>

<style scoped>
.line-clamp-2 {
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}
</style>
